import { contactSchema } from '../../../../schema/contact_schema';
import { createSelector } from 'reselect';
import CustomInput from '../../../reusable/input';
import CustomModal from '../../../modals';
import { useParams } from 'react-router';
import { AddContactParams, EditContactParams } from '../../../../params/redux/contact';
import { AppDispatch, RootState } from '../../../../redux/store';
import { Box, Button } from '@mui/material';
import { createClientContactThunk, createContactThunk, editClientContactThunk, editContactThunk } from '../../../../redux/thunks/contact';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

const selectDeal = (state: RootState) => state.deal.selectedDeal;
const selectedloaders = (state: RootState) => state.contact.loaders;

const selectedState = createSelector(
    [selectedloaders, selectDeal],
    (loaders, selectedDeal) => ({
        loaders,
        selectedDeal
    })
);

/**
 * @heading1: AddContactModal
 * @paragraph: Modal component for creating or updating a contact. Uses Formik for form handling and integrates
 * with Redux for asynchronous contact creation and updating.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */
const AddContactModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * initialValues: {StageParams} -  Initial form values for stage creation or update.
     * loaders/selectedPipeline/selectedStage: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { loaders, selectedDeal } = useSelector(selectedState);
    const { selectedContact } = useSelector((state: RootState) => state.contact);
    const isDealOrSalesPage = location.pathname.includes('/app/deal') || location.pathname.includes('/sales-marketing');
    // Initial form values for stage creation or update
    const initialValues: AddContactParams = {
        contact_name: selectedContact?.contact_name || '',
        contact_email: selectedContact?.contact_email || '',
        contact_position: selectedContact?.contact_position || '',
        contact_number: selectedContact?.contact_number?.trim() || '',
        is_primary: selectedContact?.is_primary || 0,
        // eslint-disable-next-line no-negated-condition
        deal_id: id ? 0 : selectedDeal?.id,
        client_id: id ? parseInt(id as string, 10) : 0
    };

    /**
     * @heading3: Functions
     * @heading4: Handles form submission for creating or updating a stage.
     * @param: {StageParams} values - Form values for stage creation or update.
     */
    const handleSubmit = async (values: AddContactParams) => {
        if (selectedContact) {
            const data: EditContactParams = {
                contact_id: selectedContact.id || 0,
                contact_name: values.contact_name,
                contact_email: values.contact_email,
                contact_position: values.contact_position,
                contact_number: values.contact_number,
                is_primary: values.is_primary
            };
            if (id && !isDealOrSalesPage) {
                data.client_id = parseInt(id as string, 10);
            } else {
                data.deal_id = selectedDeal?.id;
            }
            try {
                if (id && !isDealOrSalesPage) {
                    await dispatch(editClientContactThunk(data)).unwrap();
                } else {
                    await dispatch(editContactThunk(data)).unwrap();
                }
                // Close modal after successful submission
                onClose();
            } catch (e) {
                // Catch errors
            }
        } else {
            const data: AddContactParams = {
                contact_name: values.contact_name,
                contact_email: values.contact_email,
                contact_position: values.contact_position,
                contact_number: values.contact_number,
                is_primary: values.is_primary
            };
            if (id && !isDealOrSalesPage) {
                data.client_id = parseInt(id as string, 10);
            } else {
                data.deal_id = selectedDeal?.id;
            }
            try {
                if (id && !isDealOrSalesPage) {
                    await dispatch(createClientContactThunk(data)).unwrap();
                } else {
                    await dispatch(createContactThunk(data)).unwrap();
                }
                onClose();
            } catch (e) {
                // Catch errors
            }
        }
    };

    const disabled = loaders.isCreating || loaders.isUpdating;

    return <CustomModal open={open} handleClose={disabled ? () => null : onClose} title={selectedContact ? 'Edit Contact' : 'Add Contact'}>
        <Formik
            initialValues={initialValues}
            validationSchema={contactSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Input for stage name */}
                        <CustomInput
                            value={values.contact_name}
                            onChange={handleChange('contact_name')}
                            onBlur={handleBlur('contact_name')}
                            error={touched.contact_name && errors.contact_name}
                            fullWidth
                            label="Name"
                            placeholder="Type the contact's name"
                            variant="outlined"
                            disabled={disabled}
                            mandatoryFields={true}
                        />
                        {/* Input for days due */}
                        <CustomInput
                            type="email"
                            value={values.contact_email}
                            onChange={handleChange('contact_email')}
                            onBlur={handleBlur('contact_email')}
                            error={touched.contact_email && errors.contact_email}
                            fullWidth
                            label="Email Address"
                            placeholder="Type the contact's email"
                            variant="outlined"
                            disabled={disabled}
                        />
                        {/* Input for days due */}
                        <CustomInput
                            value={values.contact_position}
                            onChange={handleChange('contact_position')}
                            onBlur={handleBlur('contact_position')}
                            error={touched.contact_position && errors.contact_position}
                            fullWidth
                            label="Position in the Company"
                            placeholder="Type the contact's position"
                            variant="outlined"
                            disabled={disabled}
                        />
                        {/* Input for days due */}
                        <CustomInput
                            type="phone"
                            value={values.contact_number}
                            onChangeForPhone={handleChange('contact_number')}
                            onBlur={handleBlur('contact_number')}
                            error={touched.contact_number && errors.contact_number}
                            fullWidth
                            label="Phone Number (with country code)"
                            placeholder="201-555-0123"
                            variant="outlined"
                            mandatoryFields={true}
                            disabled={disabled}
                        />

                        <CustomInput
                            type="checkbox"
                            label="Make this Primary Contact"
                            checked={values.is_primary === 1}
                            onChange={(e) => handleChange({ target: { name: 'is_primary', value: e.target.checked ? 1 : 0 }})}
                            disabled={loaders.isCreating}
                        />
                        {/* Action buttons for cancel and submit */}
                        <Box display="flex" gap={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={disabled}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={disabled}>
                                {selectedContact && loaders.isUpdating ? 'Saving...' : selectedContact ? 'Save' : loaders.isCreating ? 'Creating...' : 'Create'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default AddContactModal;